/**
 * Collapsible Section — expandable/collapsible content wrapper for Dashboard.
 * Supports an always-visible headerContent (e.g. KPI cards) above the collapsible area.
 */
import React, { useState } from 'react';
import { Box, Typography, Collapse, IconButton } from '@mui/material';
import { ExpandMore, ChevronRight } from '@mui/icons-material';

interface CollapsibleSectionProps {
  title: string;
  defaultOpen?: boolean;
  /**
   * When true, the section is rendered open regardless of the user's
   * collapse/expand state. Used during exports so charts and content
   * inside collapsed sections still render with measurable dimensions
   * and can be captured as PNGs.
   */
  forceOpen?: boolean;
  headerContent?: React.ReactNode;
  children: React.ReactNode;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  defaultOpen = true,
  forceOpen = false,
  headerContent,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const isOpen = forceOpen || open;

  return (
    <Box sx={{ mb: 3 }}>
      {/* Editorial section header — a burgundy tick, a display title, and a
          hairline rule spanning the width, mirroring procurement's section
          dividers. */}
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          mb: 2,
          pb: 1,
          borderBottom: '1px solid var(--color-border)',
          userSelect: 'none',
          '&:hover .section-toggle': { color: 'var(--color-accent)' },
        }}
      >
        <Box
          sx={{
            width: 3,
            height: 18,
            borderRadius: '2px',
            bgcolor: 'var(--color-accent)',
            flexShrink: 0,
          }}
        />
        <Typography
          sx={{
            flex: 1,
            fontSize: 'var(--text-2xl)',
            fontWeight: 500,
            letterSpacing: 'var(--tracking-display)',
            color: 'var(--color-text-primary)',
            fontFamily: 'var(--font-heading)',
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>
        <IconButton size="small" className="section-toggle" sx={{ p: 0 }}>
          {isOpen ? <ExpandMore sx={{ fontSize: 20 }} /> : <ChevronRight sx={{ fontSize: 20 }} />}
        </IconButton>
      </Box>
      {headerContent && <Box sx={{ mb: isOpen ? 2 : 0 }}>{headerContent}</Box>}
      <Collapse in={isOpen}>{children}</Collapse>
    </Box>
  );
};

export default CollapsibleSection;
